import { useSelector } from 'react-redux';
import { Place, PlaceEmployee } from '@/types/state/shared';
import { EmployeeReviewAvatar } from '@/features/reviews/EmployeeReviewAvatar';
import Label from '@/components/elements/Label/Label';
import useBokadirektPayBadges from '@/hooks/useBokadirektPayBadges';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import { RatingV2Skeleton } from '@/components/elements/redesign/RatingV2/RatingV2Skeleton';
import { ReviewsState } from '@/reducers/reviewsReducer';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants/reviewConstants';

export const ReviewsEmployeeDetails = ({
  placeId,
  employeeId,
  className,
  reviewsUid,
}: {
  placeId: number;
  employeeId: number;
  className?: string;
  reviewsUid: string;
}) => {
  const employee = useSelector(
    (state: { employees: { all: Record<string, PlaceEmployee> } }) => state.employees.all[employeeId],
  );
  const place = useSelector(
    (state: { place: Record<number, Pick<Place, 'id' | 'lastMinuteDiscount'>> }) => state.place[placeId],
  );
  const badges = useBokadirektPayBadges(place, employee);

  const employeeReviews = useSelector(
    (state: { reviews: ReviewsState }) => state.reviews?.byUid?.[reviewsUid]?.details?.employeeStats?.[employeeId],
  );

  const hasDetails = useSelector(
    (state: { reviews: ReviewsState }) => !!state.reviews?.byUid?.[reviewsUid]?.details?.employeeStats,
  );

  const loadingByType = useSelector(
    (state: { reviews: ReviewsState }) => state.reviews?.byUid?.[reviewsUid]?.searchResult?.loadingByType,
  );

  // The modal keeps one details slot per uid and leaves it in place while refetching, so until
  // the fetch for this employee's filter lands it can hold another focus's stats (e.g. the
  // service-filtered score the user navigated from). Rating/paging fetches don't affect
  // employeeStats, so they keep showing the current score.
  const isStatsStale = !hasDetails || !!loadingByType?.switchFocus || !!loadingByType?.timeFilter;

  if (!employee) {
    return null;
  }

  const getRating = () => {
    if (!employeeReviews || employeeReviews.count < MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED) {
      return null;
    }

    if (isStatsStale) {
      return <RatingV2Skeleton size={'md'} withAverageScore withMultipleStars={false} />;
    }

    return (
      <RatingV2
        averageRating={employeeReviews?.score ?? 0}
        size={'md'}
        ratingCount={employeeReviews?.count ?? 0}
        withAverageScore={!!employeeReviews?.score}
        withMultipleStars={false}
        withLowRatingCountInformation
      />
    );
  };

  return (
    <div className={className}>
      <header className="mb-4 flex items-center gap-4">
        <EmployeeReviewAvatar employeeId={employeeId} size={'96'} />
        <div>
          <h3 className={'text-fg_primary text-h-m mb-1 font-bold'}>{employee?.about?.name}</h3>
          <h4 className={'text-fg_secondary text-m'}>{employee?.about?.jobTitle}</h4>
          {getRating()}
        </div>
      </header>
      {(badges.klarna || badges.walley) && (
        <div className="mb-4 flex flex-row gap-1">
          {badges.klarna && <Label variant="klarna" size="lg" icon={false} className="bg-bg_secondary_bold" />}
          {badges.walley && <Label variant="walley" size="lg" icon={false} className="bg-bg_secondary_bold" />}
        </div>
      )}
      <p className={'text-fg_secondary text-m'}>{employee?.about?.info}</p>
    </div>
  );
};
